/*
 * The Marketing tabs the WordPress Steward had and the suite did not (15-marketing.js, 30-dueagain.js): Dashboard,
 * Channels, Due again, Email list and Guest posts. Marketing.tsx puts them in the tab row with tabOrder.
 */
import { Link } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import {
  CHANNEL_RANGES,
  CHANNEL_WORDS,
  CHANNELS,
  mapsSpot,
  nextStep,
  PITCH_WORDS,
  sinceWords,
  type Channel,
  type ChannelsQuery,
  type ChannelTableRow,
  type DueAgainRow,
  type EmailSignupRow,
  type PitchRow,
  type PitchStatus,
} from "@steward/shared";
import { useSaveCost } from "../api/books";
import { useUpdateClient } from "../api/hooks";
import {
  useAnswerPitch,
  useChannels,
  useDueAgain,
  useEmailList,
  useMkDashboard,
  usePitches,
  useRemoveSignup,
  useSaveDueAgain,
  useSkipDue,
  useTextDue,
  useUnsub,
} from "../api/marketing-tabs";
import { useSession } from "../app/session";
import { DataTable } from "../components/DataTable";
import { Combobox, InputText } from "../components/fields";
import { money, monthName, plural, shortDate, todayIn } from "../components/format";
import { Icon, type IconName } from "../components/Icon";
import { Modal, useToast } from "../components/overlays";
import { Banner, Button, Card, Chip, EmptyState, LoadError, Skeleton, StatusLabel } from "../components/parts";

const errText = (e: unknown) => (e instanceof Error ? e.message : "That didn't work.");

// Tabs -----------------------------------------------------------------------------------------------------------------

export type MoreTab = "dash" | "channels" | "due" | "signups" | "pitches";
const MORE: Record<MoreTab, string> = {
  dash: "Dashboard",
  channels: "Channels",
  due: "Due again",
  signups: "Email list",
  pitches: "Guest posts",
};
export const isMoreTab = (t: string): t is MoreTab => t in MORE;

/**
 * The whole tab row in the old Steward's order: Dashboard first, the suite's tabs with Channels after Ranks, then Due
 * again, Lists, Email list and Guest posts. A tab the suite adds after Cold email (Campaigns) lands before Due again.
 */
export function tabOrder<T extends string>(suite: readonly (readonly [T, string])[]): [T | MoreTab | "lists", string][] {
  const out: [T | MoreTab | "lists", string][] = [["dash", MORE.dash]];
  for (const t of suite) {
    out.push([t[0], t[1]]);
    if (t[0] === "ranks") out.push(["channels", MORE.channels]);
  }
  if (!out.some(([k]) => k === "channels")) out.push(["channels", MORE.channels]);
  out.push(["due", MORE.due], ["lists", "Lists"], ["signups", MORE.signups], ["pitches", MORE.pitches]);
  return out;
}

export function MoreTabBody({ tab, onTab }: { tab: MoreTab; onTab: (t: string) => void }): ReactNode {
  if (tab === "dash") return <Dashboard onTab={onTab} />;
  if (tab === "channels") return <Channels />;
  if (tab === "due") return <DueAgain />;
  if (tab === "signups") return <EmailList />;
  return <GuestPosts />;
}

function Tile({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div className="tile">
      <span className="label">{label}</span>
      <span className="value">{value}</span>
      <span className="hint">{hint}</span>
    </div>
  );
}

/** A row that opens another tab or page (mkrow in 15-marketing.js). */
function RowButton({ icon, title, line, onClick }: { icon: IconName; title: string; line: string; onClick: () => void }) {
  return (
    <button type="button" className="setting-row mk-row" onClick={onClick}>
      <span className="mk-row-icon" aria-hidden>
        <Icon name={icon} size={18} />
      </span>
      <span className="what">
        <b>{title}</b>
        <span>{line}</span>
      </span>
      <Icon name="chevronRight" size={18} />
    </button>
  );
}

// Dashboard ------------------------------------------------------------------------------------------------------------

/** One line on the Campaigns card: name, how it is doing, On or Off. The Campaigns tab adds its own lines with this. */
export function CampaignLine({ name, line, on, onOpen }: { name: string; line: string; on: boolean; onOpen?: () => void }) {
  const body = (
    <>
      <span className="what">
        <b>{name}</b>
        <span>{line}</span>
      </span>
      <StatusLabel status={on ? "success" : "inactive"}>{on ? "On" : "Off"}</StatusLabel>
    </>
  );
  return onOpen ? (
    <button type="button" className="setting-row" onClick={onOpen}>
      {body}
    </button>
  ) : (
    <div className="setting-row">{body}</div>
  );
}

/**
 * The Dashboard (mkDashHTML and mkStatsHTML). `campaigns` is the spot for the Campaigns tab's lines (CampaignLine),
 * shown above the review request line on the Campaigns card.
 */
export function Dashboard({ onTab, campaigns }: { onTab: (t: string) => void; campaigns?: ReactNode }) {
  const { company } = useSession();
  const q = useMkDashboard(company.id);
  if (q.error) return <LoadError error={q.error} retry={() => void q.refetch()} />;
  if (!q.data) return <Skeleton rows={8} />;
  const d = q.data;
  const step = nextStep(d.posts.upcoming, d.reviewsOn);
  const spot = mapsSpot(d.ranks.map((r) => ({ now: r.average, before: r.before })));
  const scanned = d.ranks.filter((r) => r.average !== null);
  return (
    <div className="stack">
      {
        step === "posts" ? (
          <Card title="Plan your next posts">
            <div className="stack">
              <p className="muted">
                Nothing is scheduled. A post or two each week keeps the brand in front of people who already know you.
              </p>
              <div className="row">
                <Button icon="plus" onClick={() => onTab("social")}>
                  New post
                </Button>
              </div>
            </div>
          </Card>
        ) : null /* Review requests off shows in Needs you above (2026-11-01), not as a second card. */
      }
      <div className="tiles">
        <Tile label="People who reached out" value={String(d.leads30)} hint="Texts, calls and website forms, past 30 days" />
        <Tile label="New clients" value={String(d.newClients30)} hint="Past 30 days" />
        <Tile
          label="Google Maps spot"
          value={spot.average === null ? "None yet" : `#${String(spot.average)}`}
          hint={
            spot.average === null
              ? "Average over your searches"
              : `Average over ${plural(spot.searches, "search", "searches")}${
                  spot.better ? `, ${spot.better > 0 ? "up" : "down"} ${String(Math.abs(spot.better))}` : ""
                }`
          }
        />
        <Tile label="Reviews asked" value={String(d.reviewsAsked30)} hint="Past 30 days" />
      </div>
      <div className="mk-dash">
        <Card title="Coming up" flush>
          {d.posts.next.length === 0 ? (
            <p className="muted pad">No posts scheduled.</p>
          ) : (
            <ul className="auto-list">
              {d.posts.next.map((p) => (
                <li key={p.id}>
                  <button type="button" className="setting-row" onClick={() => onTab("social")}>
                    <span className="what">
                      <b>{postWhen(p.at, company.timezone)}</b>
                      <span>{p.body.slice(0, 90)}</span>
                    </span>
                    <Icon name="chevronRight" size={18} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card flush>
          <div className="mk-sec">
            <span className="mk-sec-name">Get found online</span>
            <RowButton
              icon="pin"
              title="Google profile"
              line={
                d.gbp.connected
                  ? `${d.gbp.rating === null ? "No rating yet" : `${String(d.gbp.rating)} stars`}, ${plural(d.gbp.reviewCount, "review")}${
                      d.gbp.waitingReplies ? `, ${plural(d.gbp.waitingReplies, "reply", "replies")} waiting` : ""
                    }`
                  : "Map rank shows where you come up on Google Maps around town"
              }
              onClick={() => onTab("profile")}
            />
            <RowButton
              icon="camera"
              title="Social posting"
              line={
                d.posts.next[0]
                  ? `${String(d.posts.upcoming)} scheduled, next ${shortDate(dayIn(d.posts.next[0].at, company.timezone))}`
                  : "Nothing scheduled"
              }
              onClick={() => onTab("social")}
            />
            <Link to="/requests" className="setting-row mk-row">
              <span className="mk-row-icon" aria-hidden>
                <Icon name="requests" size={18} />
              </span>
              <span className="what">
                <b>Website</b>
                <span>{`${plural(d.websiteRequests30, "request")} in the past 30 days`}</span>
              </span>
              <Icon name="chevronRight" size={18} />
            </Link>
          </div>
          <div className="mk-sec">
            <span className="mk-sec-name">Build trust</span>
            <RowButton
              icon="activity"
              title="Reviews"
              line={
                d.reviewsOn
                  ? `Review requests on, ${String(d.reviewsAsked30)} asked in the past 30 days`
                  : "Review requests are off"
              }
              onClick={() => onTab("profile")}
            />
          </div>
          <div className="mk-sec">
            <span className="mk-sec-name">Win the job</span>
            <RowButton
              icon="mail"
              title="Email list"
              line={`${String(d.emailList.onList)} on the list, ${String(d.emailList.new30)} new in the past 30 days`}
              onClick={() => onTab("signups")}
            />
          </div>
        </Card>
      </div>
      <div className="mk-dash">
        <Card
          title="Google Maps rank"
          flush
          action={
            <Button type="tertiary" size="small" onClick={() => onTab("profile")}>
              Open map
            </Button>
          }
        >
          {d.ranks.length === 0 || scanned.length === 0 ? (
            <p className="muted pad">No scans yet. Set up Map rank on the Google profile tab.</p>
          ) : (
            <ul className="auto-list">
              {d.ranks.map((r) => (
                <li key={r.keyword} className="setting-row">
                  <span className="what">
                    <b>{r.keyword}</b>
                    <span>
                      {r.average === null ? "Not scanned yet" : `Top 3 in ${String(r.top3)} of ${String(r.points)} spots`}
                    </span>
                  </span>
                  {r.average === null ? null : (
                    <span className="mk-num">
                      <b>#{r.average}</b>
                      {r.before !== null && r.before !== r.average ? (
                        <span className={r.average < r.before ? "mk-up" : "mk-down"}>
                          {r.average < r.before ? "Up" : "Down"} {Math.abs(r.before - r.average).toFixed(1)}
                        </span>
                      ) : null}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card title="Campaigns" flush>
          {campaigns}
          <CampaignLine
            name="Review requests"
            on={d.reviewsOn}
            line={`${plural(d.reviewsAsked30, "client")} asked, past 30 days`}
            onOpen={() => onTab("profile")}
          />
        </Card>
      </div>
      <Card
        title="Where leads came from"
        flush
        action={
          <Button type="tertiary" size="small" onClick={() => onTab("channels")}>
            All channels
          </Button>
        }
      >
        {d.leadsBy.length === 0 ? (
          <p className="muted pad">No leads in the past 30 days.</p>
        ) : (
          <>
            <ul className="auto-list">
              {d.leadsBy.map((r) => (
                <li key={r.channel} className="setting-row">
                  <span className="what">
                    <b>{CHANNEL_WORDS[r.channel]}</b>
                    <span>
                      {plural(r.newClients, "new client")}
                      {r.spendCents ? `, ${money(r.spendCents)} spent` : ""}
                    </span>
                  </span>
                  <span className="mk-num">
                    <b>{r.leads}</b>
                    <span className="muted">{r.leads === 1 ? "lead" : "leads"}</span>
                  </span>
                </li>
              ))}
            </ul>
            <p className="muted small pad">Past 30 days. A client counts for how they said they found you.</p>
          </>
        )}
      </Card>
    </div>
  );
}

/** The company's date of an instant. */
function dayIn(iso: string, tz: string): string {
  return new Date(iso).toLocaleDateString("en-CA", { timeZone: tz });
}
function postWhen(iso: string, tz: string): string {
  const time = new Date(iso).toLocaleTimeString("en-US", { timeZone: tz, hour: "numeric", minute: "2-digit" });
  return `${shortDate(dayIn(iso, tz))}, ${time.replace(":00", "")}`;
}

// Channels -------------------------------------------------------------------------------------------------------------

function ReturnCell({ r }: { r: ChannelTableRow }) {
  if (r.spendCents > 0)
    return (
      <span className="mk-num">
        <b className={r.netCents >= 0 ? "mk-up" : "mk-down"}>
          {r.netCents < 0 ? "-" : "+"}
          {money(Math.abs(r.netCents))}
        </b>
        <span className="muted small">{`${String(r.timesBack ?? 0)}x back`}</span>
      </span>
    );
  return r.revenueCents > 0 ? <span className="muted small">No spend</span> : <span className="muted">None</span>;
}

export function Channels() {
  const { company, can } = useSession();
  const [range, setRange] = useState<ChannelsQuery["range"]>("month");
  const [spendOpen, setSpendOpen] = useState(false);
  const q = useChannels(company.id, range);
  const label = CHANNEL_RANGES.find(([k]) => k === range)![1];
  const canSpend = can("insights.read") && can("payroll.update");
  return (
    <div className="stack">
      <div className="row between">
        <div className="row tight" role="group" aria-label="Period">
          {CHANNEL_RANGES.map(([k, l]) => (
            <Chip key={k} pressed={k === range} onClick={() => setRange(k)}>
              {l}
            </Chip>
          ))}
        </div>
        {canSpend ? (
          <Button type="secondary" onClick={() => setSpendOpen(!spendOpen)} aria-expanded={spendOpen}>
            {spendOpen ? "Close spend" : "Add what you spent"}
          </Button>
        ) : null}
      </div>
      {spendOpen ? <SpendForm onDone={() => setSpendOpen(false)} /> : null}
      {q.error ? (
        <LoadError error={q.error} retry={() => void q.refetch()} />
      ) : !q.data ? (
        <Skeleton rows={6} />
      ) : (
        <>
          <div className="tiles">
            <Tile label="Spent" value={money(q.data.totals.spendCents)} hint={label} />
            <Tile label="Leads" value={String(q.data.totals.leads)} hint="Requests and website forms" />
            <Tile label="New clients" value={String(q.data.totals.newClients)} hint={label} />
            <Tile label="Revenue" value={money(q.data.totals.revenueCents)} hint="Paid in the period" />
          </div>
          <DataTable
            title="Channels"
            rows={q.data.rows}
            rowKey={(r) => r.channel}
            empty={
              <EmptyState icon="insights" title="Nothing in this period yet">
                Leads and clients show up here by how they found you.
              </EmptyState>
            }
            columns={[
              {
                key: "c",
                header: "Channel",
                render: (r) => <b>{CHANNEL_WORDS[r.channel]}</b>,
                sort: (r) => CHANNEL_WORDS[r.channel],
              },
              {
                key: "s",
                header: "Spent",
                align: "right",
                render: (r) => (r.spendCents ? money(r.spendCents) : "None"),
                sort: (r) => r.spendCents,
              },
              { key: "l", header: "Leads", align: "right", render: (r) => r.leads, sort: (r) => r.leads },
              { key: "n", header: "New clients", align: "right", render: (r) => r.newClients, sort: (r) => r.newClients },
              { key: "r", header: "Revenue", align: "right", render: (r) => money(r.revenueCents), sort: (r) => r.revenueCents },
              { key: "a", header: "All time", align: "right", render: (r) => money(r.allTimeCents), sort: (r) => r.allTimeCents },
              {
                key: "pl",
                header: "Per lead",
                align: "right",
                render: (r) => (r.costPerLeadCents === null ? "None" : money(r.costPerLeadCents)),
                sort: (r) => r.costPerLeadCents ?? -1,
              },
              {
                key: "pc",
                header: "Per new client",
                align: "right",
                render: (r) => (r.costPerClientCents === null ? "None" : money(r.costPerClientCents)),
                sort: (r) => r.costPerClientCents ?? -1,
              },
              { key: "ret", header: "Return", align: "right", render: (r) => <ReturnCell r={r} />, sort: (r) => r.netCents },
            ]}
          />
          <p className="muted small">
            Revenue counts payments from clients by the channel they came from, so a repeat client keeps adding to it. All time is
            everything those clients have paid. Return is revenue minus what you spent.
          </p>
          {q.data.untagged.length ? <Untagged clients={q.data.untagged} /> : null}
        </>
      )}
    </div>
  );
}

/** The month's spend per channel (typed costs, the same ones Insights uses). Pulled ad spend replaces a typed month. */
function SpendForm({ onDone }: { onDone: () => void }) {
  const save = useSaveCost();
  const qc = useQueryClient();
  const toast = useToast();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [amounts, setAmounts] = useState<Partial<Record<Channel, string>>>({});
  const submit = async () => {
    try {
      for (const [channel, v] of Object.entries(amounts)) {
        if (!v.trim()) continue;
        const cents = Math.round(Number(v.replace(/[$,\s]/g, "")) * 100);
        if (!Number.isFinite(cents) || cents < 0)
          throw new Error(`Type a dollar amount for ${CHANNEL_WORDS[channel as Channel]}.`);
        await save.mutateAsync({ month, channel, amountCents: cents });
      }
      await qc.invalidateQueries({ queryKey: ["mk-channels"] });
      toast("Spend saved");
      onDone();
    } catch (e) {
      toast(errText(e), { kind: "error" });
    }
  };
  return (
    <Card title="What you spent">
      <div className="stack">
        <p className="muted">
          For each channel, the month's total. Ads, lead fees, referral rewards, printing. Leave it blank for none.
        </p>
        <InputText label="Month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
        <div className="mk-spend">
          {CHANNELS.map((c) => (
            <InputText
              key={c}
              label={CHANNEL_WORDS[c]}
              inputMode="decimal"
              prefix="$"
              value={amounts[c] ?? ""}
              onChange={(e) => setAmounts({ ...amounts, [c]: e.target.value })}
            />
          ))}
        </div>
        <div className="row end">
          <Button loading={save.isPending} onClick={() => void submit()} disabled={!/^\d{4}-\d{2}$/.test(month)}>
            {/^\d{4}-\d{2}$/.test(month) ? `Save spend for ${monthName(`${month}-01`)}` : "Save spend"}
          </Button>
        </div>
      </div>
    </Card>
  );
}

function Untagged({ clients }: { clients: { id: string; name: string }[] }) {
  const update = useUpdateClient();
  const qc = useQueryClient();
  const toast = useToast();
  return (
    <Card title="Clients with no channel">
      <div className="stack">
        <p className="muted">Pick how each one found you. It saves on their client page.</p>
        <div className="mk-spend">
          {clients.map((c) => (
            <Combobox
              key={c.id}
              label={c.name}
              value=""
              options={CHANNELS.map((k) => ({ value: k, label: CHANNEL_WORDS[k] }))}
              onChange={(k) =>
                update.mutate(
                  { id: c.id, patch: { leadSource: CHANNEL_WORDS[k as Channel] } },
                  {
                    onSuccess: () => {
                      toast(`${c.name} saved as ${CHANNEL_WORDS[k as Channel]}`);
                      void qc.invalidateQueries({ queryKey: ["mk-channels"] });
                    },
                    onError: (e) => toast(errText(e), { kind: "error" }),
                  },
                )
              }
            />
          ))}
        </div>
      </div>
    </Card>
  );
}

// Due again ------------------------------------------------------------------------------------------------------------

export function DueAgain() {
  const { company, can } = useSession();
  const q = useDueAgain(company.id);
  const skip = useSkipDue();
  const toast = useToast();
  const [texting, setTexting] = useState<DueAgainRow | null>(null);
  const edit = can("marketing.update");
  if (q.error) return <LoadError error={q.error} retry={() => void q.refetch()} />;
  if (!q.data) return <Skeleton rows={6} />;
  const d = q.data;
  const today = todayIn(company.timezone);
  return (
    <div className="stack">
      <Card title="Due again" flush>
        <p className="muted pad">
          One-time clients whose last time for a service was long enough ago that it is due again. Clients on a plan, with a visit
          booked, or set to no marketing are left off.
        </p>
        {d.list.length === 0 ? (
          <p className="muted pad">Nobody is due again right now.</p>
        ) : (
          <ul className="auto-list">
            {d.list.map((r) => (
              <li key={`${r.clientId}-${r.kind}`} className="setting-row mk-due">
                <span className="what">
                  <b>
                    <Link to="/clients/$id" params={{ id: r.clientId }}>
                      {r.name}
                    </Link>
                  </b>
                  <span>
                    {`${r.kind}, last done ${shortDate(r.last)} (${sinceWords(r.last, today)}). Due again after ${String(r.days)} days.`}
                  </span>
                  {r.textedAt ? (
                    <StatusLabel status="success">{`Texted ${shortDate(dayIn(r.textedAt, company.timezone))}`}</StatusLabel>
                  ) : null}
                </span>
                {edit ? (
                  <span className="row tight">
                    {r.hasPhone ? (
                      <Button type="secondary" size="small" onClick={() => setTexting(r)}>
                        Text
                      </Button>
                    ) : (
                      <span className="muted small">No mobile number</span>
                    )}
                    <Link className="btn tertiary small" to="/quotes/new" search={{ clientId: r.clientId }}>
                      Quote
                    </Link>
                    <Button
                      type="tertiary"
                      size="small"
                      loading={skip.isPending && skip.variables.clientId === r.clientId && skip.variables.kind === r.kind}
                      onClick={() =>
                        skip.mutate(
                          { clientId: r.clientId, kind: r.kind },
                          {
                            onSuccess: () => toast(`${r.name} is set aside until they have ${r.kind.toLowerCase()} again`),
                            onError: (e) => toast(errText(e), { kind: "error" }),
                          },
                        )
                      }
                    >
                      Not now
                    </Button>
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Card>
      {edit ? <DueSettings key={JSON.stringify(d.kinds) + d.text} kinds={d.kinds} text={d.text} /> : null}
      <Card title="Sending these by themselves" action={<Link to="/settings/automations">Change</Link>}>
        <p className="muted">
          {d.autoOn
            ? "On. The automatic Due again text goes to one-time clients when their next visit would be due."
            : "Off. Turn on the Due again text in Automatic messages to send these by themselves."}
        </p>
      </Card>
      <SendDue row={texting} hoursOk={d.hoursOk} onClose={() => setTexting(null)} />
    </div>
  );
}

function DueSettings({ kinds, text }: { kinds: { kind: string; days: number; defaultDays: number }[]; text: string }) {
  const save = useSaveDueAgain();
  const toast = useToast();
  const [days, setDays] = useState<Record<string, string>>(Object.fromEntries(kinds.map((k) => [k.kind, String(k.days)])));
  const [words, setWords] = useState(text);
  return (
    <Card title="When each service is due again">
      <div className="stack">
        <p className="muted">Days after the last time. 0 means never.</p>
        <div className="mk-spend">
          {kinds.map((k) => (
            <InputText
              key={k.kind}
              label={k.kind}
              inputMode="numeric"
              suffix="days"
              value={days[k.kind] ?? ""}
              onChange={(e) => setDays({ ...days, [k.kind]: e.target.value.replace(/\D/g, "").slice(0, 4) })}
            />
          ))}
        </div>
        <label className="field wide">
          <span className="label">The text</span>
          <textarea rows={3} maxLength={320} value={words} onChange={(e) => setWords(e.target.value)} />
          <span className="description">Fill-ins {"{first} {service} {months} {business}"}</span>
        </label>
        <div className="row end">
          <Button
            loading={save.isPending}
            onClick={() =>
              save.mutate(
                {
                  days: Object.fromEntries(Object.entries(days).map(([k, v]) => [k, Math.min(3650, Number(v) || 0)])),
                  text: words,
                },
                { onSuccess: () => toast("Saved"), onError: (e) => toast(errText(e), { kind: "error" }) },
              )
            }
          >
            Save
          </Button>
        </div>
      </div>
    </Card>
  );
}

/** The Send box: the words for this client, to read and change before anything goes. */
function SendDue({ row, hoursOk, onClose }: { row: DueAgainRow | null; hoursOk: boolean; onClose: () => void }) {
  const send = useTextDue();
  const toast = useToast();
  const [words, setWords] = useState<{ for: string; text: string } | null>(null);
  const key = row ? `${row.clientId}-${row.kind}` : "";
  const text = words?.for === key ? words.text : (row?.words ?? "");
  return (
    <Modal
      title={row ? `Text ${row.first ?? row.name}` : "Text"}
      open={row !== null}
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={send.isPending}
            disabled={!text.trim()}
            onClick={() =>
              row &&
              send.mutate(
                { clientId: row.clientId, kind: row.kind, text: text.trim() },
                {
                  onSuccess: () => {
                    toast(`Text to ${row.name} is on its way`);
                    setWords(null);
                    onClose();
                  },
                  onError: (e) => toast(errText(e), { kind: "error" }),
                },
              )
            }
          >
            Send text
          </Button>
        </>
      }
    >
      <div className="stack">
        <label className="field wide">
          <span className="label">{`The text to ${row?.first ?? row?.name ?? ""}`}</span>
          <textarea rows={4} maxLength={320} value={text} onChange={(e) => setWords({ for: key, text: e.target.value })} />
        </label>
        <p className="muted small">
          From your business line. Marketing texts go between 9 AM and 7 PM.
          {hoursOk ? "" : " It is outside those hours now, so it waits until 9 AM."}
        </p>
      </div>
    </Modal>
  );
}

// Email list -----------------------------------------------------------------------------------------------------------

/** A page address without the site, so the column stays short ("/blog/spring-list"). */
function pagePath(page: string): string {
  try {
    return new URL(page).pathname || page;
  } catch {
    return page;
  }
}

const AREA_WORDS = { in: "In your areas", out: "Outside your areas", unknown: "Not known" } as const;

function csvOf(rows: EmailSignupRow[]): string {
  const cell = (v: string | null) => `"${(v ?? "").replace(/"/g, '""')}"`;
  const lines = [["Signed up", "Email", "Name", "ZIP", "Area", "Page", "Status"].map(cell).join(",")];
  for (const s of rows)
    lines.push(
      [s.createdAt.slice(0, 10), s.email, s.name, s.zip, AREA_WORDS[s.area], s.page, s.unsub ? "Unsubscribed" : "On the list"]
        .map(cell)
        .join(","),
    );
  return lines.join("\n");
}

export function EmailList() {
  const { company, can } = useSession();
  const q = useEmailList(company.id);
  const unsub = useUnsub();
  const remove = useRemoveSignup();
  const toast = useToast();
  const [arming, setArming] = useState<string | null>(null);
  const edit = can("marketing.update");
  if (q.error) return <LoadError error={q.error} retry={() => void q.refetch()} />;
  if (!q.data) return <Skeleton rows={6} />;
  const l = q.data;
  const download = () => {
    const url = URL.createObjectURL(new Blob([csvOf(l.signups)], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "email-list.csv";
    a.click();
    URL.revokeObjectURL(url);
  };
  const flip = (s: EmailSignupRow, to: boolean) =>
    unsub.mutate(
      { id: s.id, unsub: to },
      {
        onSuccess: () =>
          toast(to ? `${s.email ?? "They"} unsubscribed` : `${s.email ?? "They"} is back on the list`, {
            undo: () => unsub.mutate({ id: s.id, unsub: !to }),
          }),
        onError: (e) => toast(errText(e), { kind: "error" }),
      },
    );
  return (
    <div className="stack">
      <Card
        title="Email list"
        action={
          <Button type="secondary" size="small" icon="file" disabled={l.signups.length === 0} onClick={download}>
            Download the list
          </Button>
        }
      >
        <div className="stack">
          <p className="muted">
            {`People who signed up through the popup or newsletter form on your website. ${String(l.onList)} on the list, ${String(l.new30)} new in the past 30 days, ${String(l.inArea)} in your areas.`}
          </p>
          <p className="muted small">If someone replies unsubscribe to an email, press Unsubscribe next to them.</p>
        </div>
      </Card>
      <DataTable
        title="Signups"
        rows={l.signups}
        rowKey={(s) => s.id}
        search={(s) => `${s.email ?? ""} ${s.name ?? ""} ${s.zip ?? ""}`}
        rowClass={(s) => (s.unsub ? "inactive" : undefined)}
        empty={
          <EmptyState icon="mail" title="No signups yet">
            They show here as people sign up on your website.
          </EmptyState>
        }
        columns={[
          { key: "at", header: "Signed up", render: (s) => shortDate(s.createdAt.slice(0, 10)), sort: (s) => s.createdAt },
          {
            key: "e",
            header: "Email",
            render: (s) => (
              <span>
                <b>{s.email ?? "No email"}</b>
                {s.name ? <span className="muted small"> {s.name}</span> : null}
              </span>
            ),
            sort: (s) => s.email ?? "",
          },
          { key: "z", header: "ZIP", render: (s) => s.zip ?? "", sort: (s) => s.zip ?? "" },
          {
            key: "a",
            header: "Area",
            render: (s) =>
              s.area === "out" ? (
                <StatusLabel status="warning">{AREA_WORDS.out}</StatusLabel>
              ) : (
                <span className="muted">{AREA_WORDS[s.area]}</span>
              ),
            sort: (s) => s.area,
          },
          {
            key: "p",
            header: "Page",
            render: (s) =>
              s.page ? (
                <a
                  href={s.page.startsWith("http") ? s.page : undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="small"
                >
                  {pagePath(s.page)}
                </a>
              ) : null,
          },
          {
            key: "s",
            header: "Status",
            render: (s) =>
              s.unsub ? (
                <StatusLabel status="inactive">Unsubscribed</StatusLabel>
              ) : (
                <StatusLabel status="success">On the list</StatusLabel>
              ),
            sort: (s) => (s.unsub ? 1 : 0),
          },
          ...(edit
            ? [
                {
                  key: "x",
                  header: "",
                  render: (s: EmailSignupRow) => (
                    <span className="row tight end mk-actions">
                      {s.email ? (
                        <Button type="tertiary" size="small" onClick={() => flip(s, !s.unsub)}>
                          {s.unsub ? "Put back on" : "Unsubscribe"}
                        </Button>
                      ) : null}
                      <Button
                        type="tertiary"
                        size="small"
                        variation={arming === s.id ? "destructive" : "subtle"}
                        loading={remove.isPending && remove.variables === s.id}
                        onBlur={() => setArming(null)}
                        onClick={() => {
                          if (arming !== s.id) {
                            setArming(s.id);
                            return;
                          }
                          remove.mutate(s.id, {
                            onSuccess: () => toast("Taken off the list"),
                            onError: (e) => toast(errText(e), { kind: "error" }),
                          });
                          setArming(null);
                        }}
                      >
                        {arming === s.id ? "Remove?" : "Remove"}
                      </Button>
                    </span>
                  ),
                },
              ]
            : []),
        ]}
      />
    </div>
  );
}

// Guest posts ----------------------------------------------------------------------------------------------------------

export function GuestPosts() {
  const { company, can } = useSession();
  const q = usePitches(company.id);
  const answer = useAnswerPitch();
  const toast = useToast();
  const edit = can("marketing.update");
  if (q.error) return <LoadError error={q.error} retry={() => void q.refetch()} />;
  if (!q.data) return <Skeleton rows={4} />;
  return (
    <div className="stack">
      <Card title="Guest post pitches">
        <p className="muted">
          Pitches from the Write for Us page on your website. Answer each one by email, then mark it here so the team knows.
        </p>
      </Card>
      <DataTable
        title="Pitches"
        rows={q.data.pitches}
        rowKey={(p) => p.id}
        search={(p) => `${p.name ?? ""} ${p.email} ${p.topic ?? ""}`}
        empty={<EmptyState icon="note" title="No pitches yet" />}
        columns={[
          { key: "at", header: "Sent", render: (p) => shortDate(p.createdAt.slice(0, 10)), sort: (p) => p.createdAt },
          {
            key: "w",
            header: "Writer",
            render: (p) => (
              <span className="stack tight">
                <b>{p.name ?? "No name"}</b>
                <span className="muted small">
                  <a href={`mailto:${p.email}`}>{p.email}</a>
                  {p.site ? `, ${p.site}` : ""}
                </span>
              </span>
            ),
            sort: (p) => p.name ?? p.email,
          },
          {
            key: "i",
            header: "Idea",
            render: (p) => (
              <span className="stack tight">
                <b>{p.topic ?? "No topic"}</b>
                {p.summary ? <span className="muted small mk-pre">{p.summary}</span> : null}
                {p.past ? <span className="muted small">{`Past writing ${p.past}`}</span> : null}
              </span>
            ),
          },
          {
            key: "a",
            header: "Answer",
            render: (p) =>
              edit ? (
                <div className="segmented" role="radiogroup" aria-label={`Answer for ${p.topic ?? p.email}`}>
                  {(Object.keys(PITCH_WORDS) as PitchStatus[]).map((s) => (
                    <button
                      key={s}
                      type="button"
                      role="radio"
                      aria-checked={p.status === s}
                      aria-selected={p.status === s}
                      onClick={() =>
                        p.status !== s &&
                        answer.mutate({ id: p.id, status: s }, { onError: (e) => toast(errText(e), { kind: "error" }) })
                      }
                    >
                      {PITCH_WORDS[s]}
                    </button>
                  ))}
                </div>
              ) : (
                <StatusLabel status={pitchStatus(p)}>{PITCH_WORDS[p.status]}</StatusLabel>
              ),
            sort: (p) => p.status,
          },
        ]}
      />
      {answer.isError ? <Banner type="error">{errText(answer.error)}</Banner> : null}
    </div>
  );
}

const pitchStatus = (p: PitchRow) => (p.status === "yes" ? "success" : p.status === "no" ? "inactive" : "informative");
